'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { IPost } from '../../../models/ICategory';
import { IPaginatedData } from '../../../models/ICommon';
import { apiService } from '../../../services/ApiService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Post from '../../components/Post';
import Loader from '../../components/svg/Loader';
import SectionTitle from '../../components/SectionTitle';
import Arrow from '../../components/svg/Arrow';
import { IAuthor } from '../../../models/IAuthor';
import { twMerge } from 'tailwind-merge';
import LoadMoreButton from '../../components/LoadMoreButton';
import { deleteQueryParams, setQueryParam, setQueryParams } from '../../../utils/querystring';

interface Props {
  entity: {
    mode: 'tag' | 'category';
    slug: string;
    title: string;
  };
  initialPosts: IPaginatedData<IPost>;
}

const celebritiesSubCategories = ['celebrities-news','cinema-media','parties'];
export default function PostsWrapper({initialPosts, entity}:Props) {
  const isCelebrities = entity.slug === 'celebrities';
  const [page, setPage] = useState(initialPosts.page);
  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    hasNextPage
  } = useInfiniteQuery({
    retry: false,
    initialData: () => {
      return {
        pageParams: [undefined, page],
        pages: [initialPosts]
      }
    },
    enabled: false,
    queryKey: [entity.mode, entity.slug,'posts'],
    queryFn: async ({ pageParam = page }) => {
      const result = await apiService.getCategoryOrTag(entity.mode, {id: entity.slug, type: 'slug'},{ page: pageParam as number, pageSize: 13 },isCelebrities ? celebritiesSubCategories : undefined);
      const isPrev = page > pageParam;
      if (isPrev) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      setPage(pageParam);
      if (result.posts.page > 1) setQueryParam({page: result.posts.page})
      else deleteQueryParams('page');
      return result.posts;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      // If the first page in our data array is page 1, there is no previous
      return firstPage.page > 1 ? firstPage.page - 1 : undefined;
    },
    getNextPageParam: (lastPage, allPages, lastPageParam, allPagesParam) => {
      if (!lastPage) return 1;
      return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
    },
    initialPageParam: page
  });



  useEffect(() => {
    if (page > 1) setQueryParam({page});
  },[page]);

const totalData = useMemo(() => {
  const allPosts = (data?.pages?.map(p => p.data).flat(Infinity) || []) as IPost[];
  return Array.from(
    new Map(allPosts.map(post => [post.uniqueCode, post])).values()
  );
}, [data]) as IPost[];

  const main = totalData?.[0];
  const subs = totalData?.slice(1);

  return (
    <div className='flex flex-col w-full max-w-[1296px] mx-auto px-[16px] desktop:px-0 gap-y-[24px]'>
      <SectionTitle title={entity.title} h1 classNames={{text: 'h2'}}/>

        {totalData?.length ? (

          <div className={twMerge('grid grid-cols-1 tablet:grid-cols-3 desktop:grid-cols-4 gap-[16px] tablet:gap-[24px]')}>
              <Post 
              isLarge
               badge={{mobile: 'image', tablet: 'image', desktop: 'image'}}
              newsType={main.newsType}
               type={main.type}
               uniqueCode={main.uniqueCode}
               key={main.uniqueCode}
               classNames={{
                 wrapper: 'row-span-1 desktop:col-span-2 desktop:row-span-2 tablet:col-span-full h-fit desktop:h-full rounded-[12px]',
                 title: 'h3',
                 image: 'h-fit desktop:flex-1 mb-0',
                 contentContainer: 'flex-shrink-0 h-fit px-[16px] py-[20px]'
                }} 
                image={{src: main.imageUrls.url, fill: true, sizes: '', priority: true, loading: 'eager', alt: main.title, className: 'rounded-tl-[12px] rounded-tr-[12px]'}} title={main.title} category={main.category}
                hide={{category: true}}
                thumbnail={main.imageUrls.thumbUrl}
                />
              {subs.map((p,i) => (
                <Post 
                isLarge
                 badge={{mobile: 'image', tablet: 'image', desktop: 'image'}}
                newsType={p.newsType}
                type={p.type}
                uniqueCode={p.uniqueCode}
                key={p.uniqueCode}
                classNames={{
                  title: 'h5',
                  wrapper: 'flex-col items-center tablet:items-start h-full rounded-[12px]',
                  image: 'tablet:h-[110px] w-fit mb-0 w-full tablet:h-[unset]',
                  contentContainer: 'h-full p-[16px]'
                  
                }} 
                image={{src: p.imageUrls.url, fill: true, sizes: '', alt: p.title, priority: i <= 1, loading: i <= 1 ? 'eager' : 'lazy', className: 'rounded-tl-[12px] rounded-tr-[12px]'}} title={p.title} category={p.category}
                hide={{category: true}}
                thumbnail={p.imageUrls.thumbUrl}
                />
              ))}
      </div>
) : null}

<div className='flex items-center justify-center mx-auto gap-[12px]'>
  {isLoading || isFetching ? <Loader/>: (
    <>
    {hasNextPage ? <LoadMoreButton text='المزيد' onClick={fetchNextPage}/> : null}
    {hasPreviousPage ? <LoadMoreButton text='السابق' onClick={fetchPreviousPage}/> : null}
    </>
  )}
</div>
</div>
)
}